import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PropertyMap = ({ properties, selectedProperty, onPropertySelect, mapCenter, onMapCenterChange }) => {
  const [mapView, setMapView] = useState('satellite');
  const [showClusters, setShowClusters] = useState(true);

  const mapStyles = {
    satellite: 'satellite',
    roadmap: 'roadmap',
    hybrid: 'hybrid',
    terrain: 'terrain'
  };

  const generateMapUrl = () => {
    const center = mapCenter || { lat: 12.9716, lng: 77.5946 }; // Bangalore default
    const zoom = 13;
    return `https://www.google.com/maps?q=${center.lat},${center.lng}&z=${zoom}&output=embed&maptype=${mapStyles[mapView]}`;
  };

  const propertyPins = properties.map(property => ({
    id: property.id,
    lat: property.coordinates.lat,
    lng: property.coordinates.lng,
    price: property.price,
    type: property.type,
    title: property.title
  }));

  return (
    <div className="relative h-full bg-surface rounded-lg overflow-hidden border border-border">
      {/* Map Controls */}
      <div className="absolute top-4 left-4 z-10 flex flex-col space-y-2">
        <div className="bg-surface border border-border rounded-lg shadow-property-card p-2">
          <div className="flex space-x-1">
            {Object.keys(mapStyles).map((style) => (
              <Button
                key={style}
                variant={mapView === style ? "default" : "ghost"}
                size="xs"
                onClick={() => setMapView(style)}
              >
                {style.charAt(0).toUpperCase() + style.slice(1)}
              </Button>
            ))}
          </div>
        </div>
        
        <div className="bg-surface border border-border rounded-lg shadow-property-card p-2">
          <Button
            variant={showClusters ? "default" : "ghost"}
            size="xs"
            onClick={() => setShowClusters(!showClusters)}
            iconName="Layers"
            iconPosition="left"
          >
            Clusters
          </Button>
        </div>
      </div>

      {/* Map View Controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col space-y-2">
        <Button
          variant="ghost"
          size="sm"
          className="bg-surface border border-border shadow-property-card"
          iconName="Plus"
        >
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="bg-surface border border-border shadow-property-card"
          iconName="Minus"
        >
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="bg-surface border border-border shadow-property-card"
          iconName="Locate"
        >
        </Button>
      </div>

      {/* Property Count Badge */}
      <div className="absolute bottom-4 left-4 z-10">
        <div className="bg-primary text-primary-foreground px-3 py-2 rounded-lg shadow-property-card">
          <div className="flex items-center space-x-2">
            <Icon name="MapPin" size={16} />
            <span className="text-sm font-medium">{properties.length} Properties</span>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 right-4 z-10">
        <div className="bg-surface border border-border rounded-lg shadow-property-card p-3">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success rounded-full"></div>
              <span className="text-xs text-text-secondary">Available</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-warning rounded-full"></div>
              <span className="text-xs text-text-secondary">Few Left</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-error rounded-full"></div>
              <span className="text-xs text-text-secondary">Sold Out</span>
            </div>
          </div>
        </div>
      </div>

      {/* Google Maps Iframe */}
      <iframe
        width="100%"
        height="100%"
        loading="lazy"
        title="Property Map"
        referrerPolicy="no-referrer-when-downgrade"
        src={generateMapUrl()}
        className="w-full h-full"
      />

      {/* Property Pin Overlays (Simulated) */}
      <div className="absolute inset-0 pointer-events-none">
        {/* This would contain actual property pins in a real implementation */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
          <div className="bg-primary text-primary-foreground px-2 py-1 rounded-lg shadow-property-card text-xs font-medium">
            ₹25,000/month
          </div>
        </div>
        
        <div className="absolute top-1/3 left-1/3 transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
          <div className="bg-secondary text-secondary-foreground px-2 py-1 rounded-lg shadow-property-card text-xs font-medium">
            ₹18,000/month
          </div>
        </div>
        
        <div className="absolute top-2/3 left-2/3 transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
          <div className="bg-accent text-accent-foreground px-2 py-1 rounded-lg shadow-property-card text-xs font-medium">
            ₹32,000/month
          </div>
        </div>
      </div>

      {/* Selected Property Info */}
      {selectedProperty && (
        <div className="absolute top-16 left-4 right-4 z-20">
          <div className="bg-surface border border-border rounded-lg shadow-modal p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-text-primary">{selectedProperty.title}</h4>
                <p className="text-sm text-text-secondary mt-1">{selectedProperty.location}</p>
                <p className="text-lg font-bold text-primary mt-2">₹{selectedProperty.price.toLocaleString()}/month</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPropertySelect(null)}
                iconName="X"
              >
              </Button>
            </div>
            <div className="flex space-x-2 mt-3">
              <Button variant="outline" size="sm" iconName="Eye" iconPosition="left">
                View Details
              </Button>
              <Button variant="default" size="sm" iconName="MessageCircle" iconPosition="left">
                Contact
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyMap;